<template>
    <div class="grid" ref="template">
        <div class="grid-full cogi-block-title">
            <cogi-label-only label="Demandeur" />
        </div>
        <cogi-personne
            cssClass="grid-full"
            v-model="chefMenage"
            :estChefMenage="true"
            :sourceItemsSexe="sourceItemsSexe"
            :sourceItemsLienParente="sourceItemsLienParente"
            :sourceItemsLienParentesEstSansSousTutelleChefOuConjointPossible="sourceItemsLienParentesEstSansSousTutelleChefOuConjointPossible"
            :sourceItemsPaysNaissance="sourceItemsPaysNaissance"
            :sourceItemsStatutResidence="sourceItemsStatutResidence"
            :sourceItemsSituation="sourceItemsSituation"
            :lienParentePourGardePartagee="model.lienParentePourGardePartagee"
            :utiliseGardePartagee="model.utiliseGardePartagee!"
            :urlPiece="urlDocumentPiecesJustificativesTraduit"
            :pourcentageGardePartage="model.pourcentageGardePartage"
            @validated="validated"
            :valideNow="valideNow"
            :culture="model.culture!">
        </cogi-personne>

        <hr class="grid-full" />

        <cogi-personnes
            cssClass="grid-full"
            v-model="model.persons!"
            :sourceItemsSexe="sourceItemsSexe"
            :sourceItemsPaysNaissance="sourceItemsPaysNaissance"
            :sourceItemsLienParente="sourceItemsLienParente"
            :sourceItemsLienParentesEstSansSousTutelleChefOuConjointPossible="sourceItemsLienParentesEstSansSousTutelleChefOuConjointPossible"
            :sourceItemsStatutResidence="sourceItemsStatutResidence"
            :sourceItemsSituation="sourceItemsSituation"
            :lienParentePourGardePartagee="model.lienParentePourGardePartagee"
            :utiliseGardePartagee="model.utiliseGardePartagee!"
            :urlPiece="urlDocumentPiecesJustificativesTraduit"
            :pourcentageGardePartage="model.pourcentageGardePartage"
            @validated="validated"
            :valideNow="valideNow"
            :culture="model.culture!" />

        <cogi-label cssClass="grid-full text-center" cssClassLabel="text-italic-small noteImportante" label="IlEstImportantDeSaisirTousLesMembres"></cogi-label>

        <cogi-button cssClass="gridspan15 right" cssClassButton="precedent" text="EtapePrecedente" @click="precedent"></cogi-button>
        <cogi-button cssClass="gridspan1 right" cssClassButton="suivant" text="EtapeSuivante" @click="suivant"></cogi-button>
    </div>
</template>
<script setup lang="ts">
    import { ref, watch, computed, getCurrentInstance } from 'vue'
    import { ValidatorAccumulatorVueComponent, ValidatorAccumulator, ValidationStatutPoco } from './helpers/validatorAccumulator'

    import CogiLabel from './CogiLabel.vue'
    import CogiPersonne from './CogiPersonne.vue'
    import CogiPersonnes from './CogiPersonnes.vue'
    import CogiButton from './CogiButton.vue'
    import CogiLabelOnly from './CogiLabelOnly.vue'
    import guid from './helpers/guidgen'
    import { del } from '../../../clients/delclient'
    import type DemandeEnLigneVM from '../DemandeEnLigneVM'

    interface IPropsCogiEtape2 {
        sourceItemsSexe: del.ListItem[]
        sourceItemsLienParente: del.ListItem[]
        sourceItemsLienParentesEstSansSousTutelleChefOuConjointPossible: Array<string>
        sourceItemsPaysNaissance: del.ListItem[]
        sourceItemsStatutResidence: del.ListItem[]
        sourceItemsSituation: del.ListItem[]
    }

    defineProps<IPropsCogiEtape2>()
    const model = defineModel<DemandeEnLigneVM>({ required: true })
    const chefMenage = ref(new del.PersonneModel({ id: guid.GenererGuid(), revenuTotal: new del.Money() }))

    const validatorAccumulator = ref(new ValidatorAccumulatorVueComponent(new ValidatorAccumulator()))
    const hasError = ref(false)
    const valideNow = ref(false)
    const isInsuivantMode = ref(false)
    const validateCallback = ref(defaultValidateCallback)
    const instance = getCurrentInstance()

    const emit = defineEmits<{
        reculerUnEtape: [number]
        avancerUnEtape: [number]
    }>()

    const urlDocumentPiecesJustificativesTraduit = computed(() => {
        return model.value.dicUrlDocumentPiecesJustificatives?.['fr'] ?? ''
    })

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function defaultValidateCallback(hasError: boolean) {}

    function suivant() {
        isInsuivantMode.value = true
        validate()
    }

    function precedent() {
        emit('reculerUnEtape', 1)
    }

    function validated(newValue: boolean) {
        let validationStatut: ValidationStatutPoco = validatorAccumulator.value.validated(newValue)

        if (!validationStatut.validationCompleted) return

        hasError.value = validationStatut.hasError
        valideNow.value = false

        doSuivant()
        validateCallback.value(hasError.value)
        validateCallback.value = defaultValidateCallback
    }

    function validate() {
        hasError.value = false
        valideNow.value = validatorAccumulator.value.startValidation(instance)
    }

    function doSuivant() {
        if (!isInsuivantMode.value) return

        isInsuivantMode.value = false
        if (hasError.value) {
            return
        } else {
            emit('avancerUnEtape', 1)
        }
    }

    watch(
        [
            () => model.value.prenom,
            () => model.value.nom,
            () => model.value.dateNaissance,
            () => model.value.revenuTotal,
            () => model.value.etudiantTempsPlein,
            () => model.value.etudiantTempsPartiel,
            () => model.value.handicapMoteur,
            () => model.value.pensionAlimentaire,
            () => model.value.pensionVeteran,
            () => model.value.assuranceInvalidite,
            () => model.value.situation,
        ],
        ([
            prenom,
            nom,
            dateNaissance,
            revenuTotal,
            etudiantTempsPlein,
            etudiantTempsPartiel,
            handicapMoteur,
            pensionAlimentaire,
            pensionVeteran,
            assuranceInvalidite,
            situation,
        ]) => {
            chefMenage.value.prenom = prenom
            chefMenage.value.nom = nom
            chefMenage.value.dateNaissance = dateNaissance
            chefMenage.value.revenuTotal = revenuTotal
            chefMenage.value.etudiantTempsPlein = etudiantTempsPlein
            chefMenage.value.etudiantTempsPartiel = etudiantTempsPartiel
            chefMenage.value.handicapMoteur = handicapMoteur
            chefMenage.value.pensionAlimentaire = pensionAlimentaire
            chefMenage.value.pensionVeteran = pensionVeteran
            chefMenage.value.assuranceInvalidite = assuranceInvalidite
            chefMenage.value.situation = situation
        },
    )

    watch(
        chefMenage.value,
        newValue => {
            model.value.prenom = newValue.prenom
            model.value.nom = newValue.nom
            model.value.dateNaissance = newValue.dateNaissance
            model.value.revenuTotal = newValue.revenuTotal
            model.value.etudiantTempsPlein = newValue.etudiantTempsPlein
            model.value.etudiantTempsPartiel = newValue.etudiantTempsPartiel
            model.value.handicapMoteur = newValue.handicapMoteur
            model.value.pensionAlimentaire = newValue.pensionAlimentaire
            model.value.pensionVeteran = newValue.pensionVeteran
            model.value.assuranceInvalidite = newValue.assuranceInvalidite
            model.value.situation = newValue.situation
        },
        { deep: true },
    )

    defineExpose({ validate, hasError, validateCallback })
</script>
